import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { DineInSessionMember } from "../../../../interfaces/DineInObject";
import "./styles/DineInMemberList.css";

interface DineInMemberListProps {
  members: DineInSessionMember[];
  /** DineInSession.creatorId — whoever opened the table gets a "Host" badge. */
  creatorId: string;
  currentUserId?: string;
}

/**
 * "N people ordering" trigger + a small popover listing everyone at the
 * table. Same trigger/menu/click-outside shape as shared/Dropdown, but a
 * read-only info list rather than a selectable options menu — its own
 * component rather than forcing Dropdown's {id,label}+onChange contract onto
 * something that doesn't select anything (Dropdown.tsx's own header comment
 * already draws that line for BusinessDropdown/SectionSelect).
 */
const DineInMemberList: React.FC<DineInMemberListProps> = ({
  members,
  creatorId,
  currentUserId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div className="dine-in-member-list" ref={ref}>
      <button
        type="button"
        className={`dine-in-member-list__trigger${
          isOpen ? " dine-in-member-list__trigger--open" : ""
        }`}
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {members.length} {members.length === 1 ? "person" : "people"} ordering
        <ChevronDown size={14} strokeWidth={2.5} className="dine-in-member-list__arrow" />
      </button>

      {isOpen && (
        <div className="dine-in-member-list__menu" role="menu">
          {members.map((member) => {
            const isYou = !!currentUserId && member.memberId === currentUserId;
            return (
              <div
                key={member.memberId}
                className={`dine-in-member-list__row${
                  isYou ? " dine-in-member-list__row--you" : ""
                }`}
                role="menuitem"
              >
                <span className="dine-in-member-list__avatar">
                  {member.memberName.slice(0, 2).toUpperCase()}
                </span>
                <span className="dine-in-member-list__name">
                  {isYou ? "You" : member.memberName}
                </span>
                {member.memberId === creatorId && (
                  <span className="dine-in-member-list__badge">Host</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DineInMemberList;
